/**
 * Utilitaires MSAL côté client : marqueur OAuth, parsing des claims, hydratation Pinia.
 * Tout le module suppose `import.meta.client` là où la session ou le DOM sont nécessaires.
 */

import type { AccountProfile, UserProfileDto } from '~/types/AccountProfile'
import type { MsalIdTokenClaims } from '~/types/msal'
import { useUserStore } from '~/stores/user'
import { authCookies } from '~/utils/authCookies'

// ── idTokenClaims ───────────────────────────────────────────────────────────

/**
 * Indique si la valeur est un objet non-tableau indexable comme enregistrement.
 * @param {unknown} value - Valeur à tester.
 * @returns {value is Record<string, unknown>} Vrai si objet « plat » exploitable.
 */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Normalise un claim booléen Entra (`true`, `"true"`, etc.).
 * @param {unknown} value - Valeur brute du claim.
 * @returns {boolean | undefined} Booléen ou `undefined` si non reconnu.
 */
function parseBooleanClaim(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true
  if (value === false || value === 'false') return false
  return undefined
}

/**
 * Convertit la valeur brute `idTokenClaims` MSAL en objet typé pour l'app (sans assertion `as`).
 * @param {unknown} value - Valeur retournée par MSAL (souvent un objet, parfois absente).
 * @returns {MsalIdTokenClaims} Champs reconnus uniquement ; objet vide sinon.
 */
export function toMsalIdTokenClaims(value: unknown): MsalIdTokenClaims {
  if (!isPlainRecord(value)) {
    return {}
  }

  const out: MsalIdTokenClaims = {}

  if (typeof value.oid === 'string') {
    out.oid = value.oid
  }

  if (typeof value.idp === 'string' && value.idp.trim() !== '') {
    out.idp = value.idp
  }

  const newUser: boolean | undefined = parseBooleanClaim(value.newUser)
  if (newUser !== undefined) {
    out.newUser = newUser
  }

  const isNewUser: boolean | undefined = parseBooleanClaim(value.isNewUser)
  if (isNewUser !== undefined) {
    out.isNewUser = isNewUser
  }

  if ('extension_mbti' in value) {
    const m = value.extension_mbti
    if (m === null) {
      out.extension_mbti = null
    } else if (typeof m === 'string') {
      out.extension_mbti = m
    }
  }

  if (
    typeof value.extension_profileCompletion === 'number' &&
    !Number.isNaN(value.extension_profileCompletion)
  ) {
    out.extension_profileCompletion = value.extension_profileCompletion
  }

  return out
}

// ── Hydratation store depuis MSAL ───────────────────────────────────────────

/**
 * Si le store utilisateur est vide mais que MSAL a déjà un compte (cache session),
 * hydrate le store à partir du compte actif sans nouvelle interaction.
 * @returns {boolean} `true` si une session est considérée comme présente (déjà hydraté ou hydraté ici), sinon `false`.
 */
export async function hydrateUserStoreFromMsalIfNeeded(): Promise<boolean> {
  if (!import.meta.client) return false
  const userStore = useUserStore()
  if (userStore.isLoggedIn) return true
  try {
    const msal = await useNuxtApp().$msalReady
    if (!msal) return false
    const accounts = msal.getAllAccounts()
    const account = accounts[0]
    if (account === undefined) return false
    msal.setActiveAccount(account)
    const claims = toMsalIdTokenClaims(account.idTokenClaims)
    userStore.login({
      userId: claims.oid ?? account.localAccountId,
      firstName: account.name?.split(' ')[0] ?? 'Étudiant',
      avatar: null,
      mbti: claims.extension_mbti ?? null,
      profileCompletion: claims.extension_profileCompletion ?? 0,
      token: null,
    })
    authCookies.setHasAccount(true)
    return true
  } catch {
    return false
  }
}

// ── GET /api/Profile/me → AccountProfile ─────────────────────────────────────

/**
 * E-mail du compte MSAL actif (`/api/Profile/me` ne le renvoie pas).
 * @returns {string | null} E-mail ou `null` (hors client ou compte absent).
 */
export async function getMsalActiveAccountEmail(): Promise<string | null> {
  if (!import.meta.client) {
    return null
  }
  try {
    const msalUnknown: unknown = await useNuxtApp().$msalReady
    if (
      msalUnknown === null ||
      typeof msalUnknown !== 'object' ||
      !('getActiveAccount' in msalUnknown) ||
      typeof (msalUnknown as { getActiveAccount: unknown }).getActiveAccount !== 'function'
    ) {
      return null
    }
    const username: string | undefined = (
      msalUnknown as { getActiveAccount: () => { username?: string } | null }
    ).getActiveAccount()?.username
    return typeof username === 'string' && username.includes('@') ? username : null
  } catch {
    return null
  }
}

/**
 * Normalise la réponse `GET /api/Profile/me` vers {@link AccountProfile}.
 * L'e-mail provient de MSAL si l'API ne le renvoie pas.
 * @param {UserProfileDto | null | undefined} data - Corps JSON de l'API.
 * @param {string | null} fallbackEmail - E-mail MSAL.
 * @returns {AccountProfile} Profil pour l'UI Mon compte.
 */
export function toAccountProfileFromProfilMe(
  data: UserProfileDto | null | undefined,
  fallbackEmail: string | null,
): AccountProfile {
  const apiEmail: string | undefined = data?.email?.trim()
  const apiPhone: string | undefined = data?.phone?.trim()

  return {
    email: apiEmail !== undefined && apiEmail !== '' ? apiEmail : (fallbackEmail ?? ''),
    phone: apiPhone !== undefined && apiPhone !== '' ? apiPhone : null,
    isEmailVerified: data?.emailVerified ?? false,
    isPhoneVerified: data?.phoneVerified ?? false,
  }
}
